import { Controller } from "@hotwired/stimulus"

// Pre-checks a picked file against the input's accept list and a byte limit,
// so the user hears "that image is 3.4MB — the limit is 2MB" on pick instead of
// after a failed submit. A rejected pick is cleared from the input; server-side
// validation stays authoritative.
export default class extends Controller {
  static targets = ["input", "feedback"]
  static values = { maxBytes: Number }

  check() {
    const problems = Array.from(this.inputTarget.files).flatMap(file => this.problemsFor(file))

    if (problems.length === 0) {
      this.clearFeedback()
    } else {
      this.inputTarget.value = ""
      this.showFeedback(problems.join(" "))
    }
  }

  problemsFor(file) {
    const problems = []
    if (!this.typeAccepted(file)) {
      problems.push(`${file.name} isn't a supported file type.`)
    } else if (this.maxBytesValue > 0 && file.size > this.maxBytesValue) {
      problems.push(`${file.name} is ${this.formatBytes(file.size)} — the limit is ${this.formatBytes(this.maxBytesValue)}.`)
    }
    return problems
  }

  typeAccepted(file) {
    const accept = (this.inputTarget.getAttribute("accept") || "").trim()
    if (accept === "") return true

    const patterns = accept.split(",").map(pattern => pattern.trim()).filter(pattern => pattern !== "")
    // Some clients report no MIME type at all; a MIME pattern can never match
    // then, so judge by extension patterns alone — and if there are none, let
    // the authoritative server-side validation decide.
    if (file.type === "") {
      const extensions = patterns.filter(pattern => pattern.startsWith("."))
      return extensions.length === 0 || extensions.some(pattern => this.matchesAccept(file, pattern))
    }
    return patterns.some(pattern => this.matchesAccept(file, pattern))
  }

  matchesAccept(file, pattern) {
    if (pattern.startsWith(".")) return file.name.toLowerCase().endsWith(pattern.toLowerCase())
    if (pattern.endsWith("/*")) return file.type.startsWith(pattern.slice(0, -1))
    return file.type === pattern
  }

  // Rounds up, so a file just over the limit can never display as equal to it
  // ("is 2.1MB — the limit is 2.0MB", never "is 2.0MB — the limit is 2.0MB").
  formatBytes(bytes) {
    if (bytes >= 1024 * 1024) return `${(Math.ceil((bytes / (1024 * 1024)) * 10) / 10).toFixed(1)}MB`
    if (bytes >= 1024) return `${Math.ceil(bytes / 1024)}KB`
    return `${bytes} bytes`
  }

  showFeedback(message) {
    this.inputTarget.classList.add("is-invalid")
    if (this.hasFeedbackTarget) this.feedbackTarget.textContent = message
  }

  clearFeedback() {
    this.inputTarget.classList.remove("is-invalid")
    if (this.hasFeedbackTarget) this.feedbackTarget.textContent = ""
  }
}
